/**
 * Media / encoding domain events — catalog 1.16.0 additions, completed with
 * the "catalog v12" delta in 1.18.0 (commerce-platform phase-8a seam: media
 * events → gateway WS bridge; P4-T06).
 *
 * NOTE (catalog release naming, mirrors drift notes R-5 in catalog/email.ts and
 * the "catalog v12" label in phase-8a §5): the plan's "v12" is plan fiction.
 * The latest real catalog release is 1.15.0 (`email:received`), so the first
 * five definitions landed in the next real release, `since: '1.16.0'`. The
 * eight v12-completion events (title/asset/job/subtitle lifecycle below) land
 * in `since: '1.18.0'` because 1.17.0 is already taken by `subscriptions:*`
 * (catalog v11, P3-T6).
 *
 * Transport: `media:job-progress` and `media:playback-token-issued` STAY
 * EXCLUDED from durable fan-out — both are high-frequency, have no audit row,
 * and travel the raw Redis channel `media:events` ONLY, into the gateway's
 * dedicated `WsMediaEventsBridge`
 * (storage-bridge precedent); they are listed in FANOUT_EXCLUDED_EVENT_TYPES so a
 * future durable relay never doubles them onto `events:fanout`. The remaining
 * types (including every 1.18.0 and 1.19.0 addition except the token tick
 * above) ride the same channel today and are
 * catalog-registered so the audit tailer and payload schemas have a published
 * contract.
 *
 * Fan-out room: `ROOM_MEDIA_UPDATES` (`media:updates`), ORG-SCOPED per master
 * A13 — the bridge appends `:${payload.orgId}`; an event without `orgId` is
 * not fanned out at all (fail-closed, no cross-tenant broadcast).
 *
 * - `media:asset-registered` — a media track row was registered against a
 *   title/episode (never-muxed rule: one track per row, phase-8a §5).
 * - `media:job-queued`       — an encode job was accepted onto the queue.
 * - `media:job-progress`     — encoder progress tick (observability-only:
 *   auditable:false; high-frequency, excluded from durable fan-out).
 * - `media:job-completed`    — an encode job reached success (outputs live in
 *   storage-service; the payload references ids only, never bytes).
 * - `media:job-failed`       — an encode job failed (reason string, no
 *   credential material or file paths).
 *
 * Catalog v12 completion (P4-T06, `since: '1.18.0'`) — auditable lifecycle
 * events for the catalog surface:
 *
 * - `media:title-published`  — publish state machine (or sweeper via the
 *   media backplane) moved a title to published.
 * - `media:title-scheduled`  — a title entered the `scheduled` state with a
 *   future `publishAt`.
 * - `media:title-hidden`     — a title was hidden from the catalog surface.
 * - `media:title-archived`   — a title was archived (terminal-ish, reversible
 *   by staff only).
 * - `media:asset-probed`     — ffprobe finished for a registered asset
 *   (technical metadata available; ids only, never bytes).
 * - `media:asset-ready`      — an asset became ready for publishing.
 * - `media:job-cancelled`    — an encode job was cancelled before completion.
 * - `media:subtitle-imported`— a subtitle file was imported against an asset.
 *
 * Catalog "v13" additions (P5-T06, phase-8b, `since: '1.19.0'` — the v13 label
 * is the same plan fiction as v12; 1.18.0 was the last real release):
 *
 * - `media:packaging-completed` — a media-package job finished (HLS/DASH
 *   outputs referenced by ids only; emitted by encoding-worker when the
 *   pipeline lands, and by the scaffold today with a reason tag).
 * - `media:packaging-failed`    — a media-package job failed (reason string,
 *   no credential material or file paths).
 * - `media:playback-token-issued` — a short-lived playback token was minted
 *   (auditable:false — high frequency; same `media:job-progress` precedent:
 *   raw Redis channel ONLY, listed in FANOUT_EXCLUDED_EVENT_TYPES so a future
 *   durable relay never doubles it onto `events:fanout`).
 */
import {
  asEventRecord,
  defineEvent,
  optionalNumber,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface MediaAssetRegisteredPayload extends Record<string, unknown> {
  orgId: string;
  assetId: string;
  ownerType: string;
  ownerId: string;
  trackType: string;
  registeredAt: string;
}

export interface MediaJobQueuedPayload extends Record<string, unknown> {
  orgId: string;
  jobId: string;
  assetId?: string;
  kind: string;
  queuedAt: string;
}

export interface MediaJobProgressPayload extends Record<string, unknown> {
  orgId: string;
  jobId: string;
  percent: number;
  stage?: string;
  at: string;
}

export interface MediaJobCompletedPayload extends Record<string, unknown> {
  orgId: string;
  jobId: string;
  assetId?: string;
  outputStorageFileId?: string;
  completedAt: string;
}

export interface MediaJobFailedPayload extends Record<string, unknown> {
  orgId: string;
  jobId: string;
  assetId?: string;
  reason: string;
  failedAt: string;
}

const ASSET_REGISTERED_TYPE = 'media:asset-registered';
const assetRegisteredSchema: EventSchema<MediaAssetRegisteredPayload> = {
  parse(data) {
    const record = asEventRecord(data, ASSET_REGISTERED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', ASSET_REGISTERED_TYPE),
      assetId: requireString(record.assetId, 'assetId', ASSET_REGISTERED_TYPE),
      ownerType: requireString(record.ownerType, 'ownerType', ASSET_REGISTERED_TYPE),
      ownerId: requireString(record.ownerId, 'ownerId', ASSET_REGISTERED_TYPE),
      trackType: requireString(record.trackType, 'trackType', ASSET_REGISTERED_TYPE),
      registeredAt: requireString(record.registeredAt, 'registeredAt', ASSET_REGISTERED_TYPE),
    };
  },
};

export const MEDIA_ASSET_REGISTERED_EVENT = defineEvent<MediaAssetRegisteredPayload>({
  type: ASSET_REGISTERED_TYPE,
  version: 1,
  schema: assetRegisteredSchema,
  auditable: true,
  description:
    'A media track asset was registered against a title or episode (one track per row; ids only, never bytes).',
  since: '1.16.0',
});

const JOB_QUEUED_TYPE = 'media:job-queued';
const jobQueuedSchema: EventSchema<MediaJobQueuedPayload> = {
  parse(data) {
    const record = asEventRecord(data, JOB_QUEUED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', JOB_QUEUED_TYPE),
      jobId: requireString(record.jobId, 'jobId', JOB_QUEUED_TYPE),
      assetId: optionalString(record.assetId, 'assetId', JOB_QUEUED_TYPE),
      kind: requireString(record.kind, 'kind', JOB_QUEUED_TYPE),
      queuedAt: requireString(record.queuedAt, 'queuedAt', JOB_QUEUED_TYPE),
    };
  },
};

export const MEDIA_JOB_QUEUED_EVENT = defineEvent<MediaJobQueuedPayload>({
  type: JOB_QUEUED_TYPE,
  version: 1,
  schema: jobQueuedSchema,
  auditable: true,
  description: 'An encoding worker job was accepted onto its queue.',
  since: '1.16.0',
});

const JOB_PROGRESS_TYPE = 'media:job-progress';
const jobProgressSchema: EventSchema<MediaJobProgressPayload> = {
  parse(data) {
    const record = asEventRecord(data, JOB_PROGRESS_TYPE);
    const percent = record.percent;
    if (typeof percent !== 'number' || !Number.isFinite(percent) || percent < 0 || percent > 100) {
      throw new Error(`${JOB_PROGRESS_TYPE}: field "percent" must be a number between 0 and 100`);
    }
    return {
      orgId: requireString(record.orgId, 'orgId', JOB_PROGRESS_TYPE),
      jobId: requireString(record.jobId, 'jobId', JOB_PROGRESS_TYPE),
      percent,
      stage: optionalString(record.stage, 'stage', JOB_PROGRESS_TYPE),
      at: requireString(record.at, 'at', JOB_PROGRESS_TYPE),
    };
  },
};

export const MEDIA_JOB_PROGRESS_EVENT = defineEvent<MediaJobProgressPayload>({
  type: JOB_PROGRESS_TYPE,
  version: 1,
  schema: jobProgressSchema,
  auditable: false,
  description:
    'Encoder progress tick (high-frequency; Redis-only transport, excluded from durable fan-out and from audit).',
  since: '1.16.0',
});

const JOB_COMPLETED_TYPE = 'media:job-completed';
const jobCompletedSchema: EventSchema<MediaJobCompletedPayload> = {
  parse(data) {
    const record = asEventRecord(data, JOB_COMPLETED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', JOB_COMPLETED_TYPE),
      jobId: requireString(record.jobId, 'jobId', JOB_COMPLETED_TYPE),
      assetId: optionalString(record.assetId, 'assetId', JOB_COMPLETED_TYPE),
      outputStorageFileId: optionalString(
        record.outputStorageFileId,
        'outputStorageFileId',
        JOB_COMPLETED_TYPE,
      ),
      completedAt: requireString(record.completedAt, 'completedAt', JOB_COMPLETED_TYPE),
    };
  },
};

export const MEDIA_JOB_COMPLETED_EVENT = defineEvent<MediaJobCompletedPayload>({
  type: JOB_COMPLETED_TYPE,
  version: 1,
  schema: jobCompletedSchema,
  auditable: true,
  description:
    'An encoding job completed; outputs are referenced by storage file id only (never bytes or paths).',
  since: '1.16.0',
});

const JOB_FAILED_TYPE = 'media:job-failed';
const jobFailedSchema: EventSchema<MediaJobFailedPayload> = {
  parse(data) {
    const record = asEventRecord(data, JOB_FAILED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', JOB_FAILED_TYPE),
      jobId: requireString(record.jobId, 'jobId', JOB_FAILED_TYPE),
      assetId: optionalString(record.assetId, 'assetId', JOB_FAILED_TYPE),
      reason: requireString(record.reason, 'reason', JOB_FAILED_TYPE),
      failedAt: requireString(record.failedAt, 'failedAt', JOB_FAILED_TYPE),
    };
  },
};

export const MEDIA_JOB_FAILED_EVENT = defineEvent<MediaJobFailedPayload>({
  type: JOB_FAILED_TYPE,
  version: 1,
  schema: jobFailedSchema,
  auditable: true,
  description: 'An encoding job failed (reason string only; no credentials, no filesystem paths).',
  since: '1.16.0',
});

// ---------------------------------------------------------------------------
// Catalog v12 completion (P4-T06) — `since: '1.18.0'` (1.17.0 is taken by the
// subscriptions:* set; the plan's "v12" label is drift, see header note).
// All eight are auditable and ride the same transports as the 1.16.0 subset.
// ---------------------------------------------------------------------------

export interface MediaTitlePublishedPayload extends Record<string, unknown> {
  orgId: string;
  titleId: string;
  status: string;
  publishedAt?: string;
}

export interface MediaTitleScheduledPayload extends Record<string, unknown> {
  orgId: string;
  titleId: string;
  status: string;
  publishedAt?: string;
}

export interface MediaTitleHiddenPayload extends Record<string, unknown> {
  orgId: string;
  titleId: string;
  status: string;
  publishedAt?: string;
}

export interface MediaTitleArchivedPayload extends Record<string, unknown> {
  orgId: string;
  titleId: string;
  status: string;
  publishedAt?: string;
}

export interface MediaAssetProbedPayload extends Record<string, unknown> {
  orgId: string;
  assetId: string;
  ownerType: string;
  ownerId: string;
  trackType: string;
  status: string;
}

export interface MediaAssetReadyPayload extends Record<string, unknown> {
  orgId: string;
  assetId: string;
  ownerType: string;
  ownerId: string;
  trackType: string;
  status: string;
}

export interface MediaJobCancelledPayload extends Record<string, unknown> {
  orgId: string;
  jobId: string;
  status: string;
}

export interface MediaSubtitleImportedPayload extends Record<string, unknown> {
  orgId: string;
  assetId: string;
  ownerId: string;
  language: string;
  format: string;
}

const TITLE_PUBLISHED_TYPE = 'media:title-published';
const titlePublishedSchema: EventSchema<MediaTitlePublishedPayload> = {
  parse(data) {
    const record = asEventRecord(data, TITLE_PUBLISHED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', TITLE_PUBLISHED_TYPE),
      titleId: requireString(record.titleId, 'titleId', TITLE_PUBLISHED_TYPE),
      status: requireString(record.status, 'status', TITLE_PUBLISHED_TYPE),
      publishedAt: optionalString(record.publishedAt, 'publishedAt', TITLE_PUBLISHED_TYPE),
    };
  },
};

export const MEDIA_TITLE_PUBLISHED_EVENT = defineEvent<MediaTitlePublishedPayload>({
  type: TITLE_PUBLISHED_TYPE,
  version: 1,
  schema: titlePublishedSchema,
  auditable: true,
  description:
    'A media title transitioned to published through the publish state machine or the due-scheduled sweeper (ids only).',
  since: '1.18.0',
});

const TITLE_SCHEDULED_TYPE = 'media:title-scheduled';
const titleScheduledSchema: EventSchema<MediaTitleScheduledPayload> = {
  parse(data) {
    const record = asEventRecord(data, TITLE_SCHEDULED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', TITLE_SCHEDULED_TYPE),
      titleId: requireString(record.titleId, 'titleId', TITLE_SCHEDULED_TYPE),
      status: requireString(record.status, 'status', TITLE_SCHEDULED_TYPE),
      publishedAt: optionalString(record.publishedAt, 'publishedAt', TITLE_SCHEDULED_TYPE),
    };
  },
};

export const MEDIA_TITLE_SCHEDULED_EVENT = defineEvent<MediaTitleScheduledPayload>({
  type: TITLE_SCHEDULED_TYPE,
  version: 1,
  schema: titleScheduledSchema,
  auditable: true,
  description: 'A media title entered the scheduled state with a future publishAt (ids only).',
  since: '1.18.0',
});

const TITLE_HIDDEN_TYPE = 'media:title-hidden';
const titleHiddenSchema: EventSchema<MediaTitleHiddenPayload> = {
  parse(data) {
    const record = asEventRecord(data, TITLE_HIDDEN_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', TITLE_HIDDEN_TYPE),
      titleId: requireString(record.titleId, 'titleId', TITLE_HIDDEN_TYPE),
      status: requireString(record.status, 'status', TITLE_HIDDEN_TYPE),
      publishedAt: optionalString(record.publishedAt, 'publishedAt', TITLE_HIDDEN_TYPE),
    };
  },
};

export const MEDIA_TITLE_HIDDEN_EVENT = defineEvent<MediaTitleHiddenPayload>({
  type: TITLE_HIDDEN_TYPE,
  version: 1,
  schema: titleHiddenSchema,
  auditable: true,
  description: 'A media title was hidden from the public catalog surface (ids only).',
  since: '1.18.0',
});

const TITLE_ARCHIVED_TYPE = 'media:title-archived';
const titleArchivedSchema: EventSchema<MediaTitleArchivedPayload> = {
  parse(data) {
    const record = asEventRecord(data, TITLE_ARCHIVED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', TITLE_ARCHIVED_TYPE),
      titleId: requireString(record.titleId, 'titleId', TITLE_ARCHIVED_TYPE),
      status: requireString(record.status, 'status', TITLE_ARCHIVED_TYPE),
      publishedAt: optionalString(record.publishedAt, 'publishedAt', TITLE_ARCHIVED_TYPE),
    };
  },
};

export const MEDIA_TITLE_ARCHIVED_EVENT = defineEvent<MediaTitleArchivedPayload>({
  type: TITLE_ARCHIVED_TYPE,
  version: 1,
  schema: titleArchivedSchema,
  auditable: true,
  description: 'A media title was archived (staff-reversible terminal catalog state; ids only).',
  since: '1.18.0',
});

const ASSET_PROBED_TYPE = 'media:asset-probed';
const assetProbedSchema: EventSchema<MediaAssetProbedPayload> = {
  parse(data) {
    const record = asEventRecord(data, ASSET_PROBED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', ASSET_PROBED_TYPE),
      assetId: requireString(record.assetId, 'assetId', ASSET_PROBED_TYPE),
      ownerType: requireString(record.ownerType, 'ownerType', ASSET_PROBED_TYPE),
      ownerId: requireString(record.ownerId, 'ownerId', ASSET_PROBED_TYPE),
      trackType: requireString(record.trackType, 'trackType', ASSET_PROBED_TYPE),
      status: requireString(record.status, 'status', ASSET_PROBED_TYPE),
    };
  },
};

export const MEDIA_ASSET_PROBED_EVENT = defineEvent<MediaAssetProbedPayload>({
  type: ASSET_PROBED_TYPE,
  version: 1,
  schema: assetProbedSchema,
  auditable: true,
  description:
    'A probe (ffprobe) pass completed for a registered media asset; technical metadata is available (ids and status only).',
  since: '1.18.0',
});

const ASSET_READY_TYPE = 'media:asset-ready';
const assetReadySchema: EventSchema<MediaAssetReadyPayload> = {
  parse(data) {
    const record = asEventRecord(data, ASSET_READY_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', ASSET_READY_TYPE),
      assetId: requireString(record.assetId, 'assetId', ASSET_READY_TYPE),
      ownerType: requireString(record.ownerType, 'ownerType', ASSET_READY_TYPE),
      ownerId: requireString(record.ownerId, 'ownerId', ASSET_READY_TYPE),
      trackType: requireString(record.trackType, 'trackType', ASSET_READY_TYPE),
      status: requireString(record.status, 'status', ASSET_READY_TYPE),
    };
  },
};

export const MEDIA_ASSET_READY_EVENT = defineEvent<MediaAssetReadyPayload>({
  type: ASSET_READY_TYPE,
  version: 1,
  schema: assetReadySchema,
  auditable: true,
  description:
    'A media asset became ready for publishing (encoded/probed; references ids only, never bytes).',
  since: '1.18.0',
});

const JOB_CANCELLED_TYPE = 'media:job-cancelled';
const jobCancelledSchema: EventSchema<MediaJobCancelledPayload> = {
  parse(data) {
    const record = asEventRecord(data, JOB_CANCELLED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', JOB_CANCELLED_TYPE),
      jobId: requireString(record.jobId, 'jobId', JOB_CANCELLED_TYPE),
      status: requireString(record.status, 'status', JOB_CANCELLED_TYPE),
    };
  },
};

export const MEDIA_JOB_CANCELLED_EVENT = defineEvent<MediaJobCancelledPayload>({
  type: JOB_CANCELLED_TYPE,
  version: 1,
  schema: jobCancelledSchema,
  auditable: true,
  description: 'An encoding job was cancelled before completion (ids and terminal status only).',
  since: '1.18.0',
});

const SUBTITLE_IMPORTED_TYPE = 'media:subtitle-imported';
const subtitleImportedSchema: EventSchema<MediaSubtitleImportedPayload> = {
  parse(data) {
    const record = asEventRecord(data, SUBTITLE_IMPORTED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', SUBTITLE_IMPORTED_TYPE),
      assetId: requireString(record.assetId, 'assetId', SUBTITLE_IMPORTED_TYPE),
      ownerId: requireString(record.ownerId, 'ownerId', SUBTITLE_IMPORTED_TYPE),
      language: requireString(record.language, 'language', SUBTITLE_IMPORTED_TYPE),
      format: requireString(record.format, 'format', SUBTITLE_IMPORTED_TYPE),
    };
  },
};

export const MEDIA_SUBTITLE_IMPORTED_EVENT = defineEvent<MediaSubtitleImportedPayload>({
  type: SUBTITLE_IMPORTED_TYPE,
  version: 1,
  schema: subtitleImportedSchema,
  auditable: true,
  description:
    'A subtitle file was imported against a media asset (language/format metadata and ids only, never file content).',
  since: '1.18.0',
});

// ---------------------------------------------------------------------------
// Catalog "v13" additions (P5-T06, phase-8b) — `since: '1.19.0'` (the last real
// release was 1.18.0; the plan's "v13" label is drift, see header note).
// ---------------------------------------------------------------------------

export interface MediaPackagingCompletedPayload extends Record<string, unknown> {
  orgId: string;
  jobId: string;
  assetId?: string;
  /** Number of rendition rungs packaged (count only, never bytes or paths). */
  targets?: number;
  completedAt: string;
}

export interface MediaPackagingFailedPayload extends Record<string, unknown> {
  orgId: string;
  jobId: string;
  assetId?: string;
  reason: string;
  failedAt: string;
}

export interface MediaPlaybackTokenIssuedPayload extends Record<string, unknown> {
  orgId: string;
  assetId: string;
  /** Requesting user id (subject of the minted short-lived token). */
  userId?: string;
  /** Whole seconds granted to the token (never the token itself). */
  expiresIn?: number;
  issuedAt: string;
}

const PACKAGING_COMPLETED_TYPE = 'media:packaging-completed';
const packagingCompletedSchema: EventSchema<MediaPackagingCompletedPayload> = {
  parse(data) {
    const record = asEventRecord(data, PACKAGING_COMPLETED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', PACKAGING_COMPLETED_TYPE),
      jobId: requireString(record.jobId, 'jobId', PACKAGING_COMPLETED_TYPE),
      assetId: optionalString(record.assetId, 'assetId', PACKAGING_COMPLETED_TYPE),
      targets: optionalNumber(record.targets, 'targets', PACKAGING_COMPLETED_TYPE),
      completedAt: requireString(record.completedAt, 'completedAt', PACKAGING_COMPLETED_TYPE),
    };
  },
};

export const MEDIA_PACKAGING_COMPLETED_EVENT = defineEvent<MediaPackagingCompletedPayload>({
  type: PACKAGING_COMPLETED_TYPE,
  version: 1,
  schema: packagingCompletedSchema,
  auditable: true,
  description:
    'A media-package job completed; HLS/DASH outputs are referenced by ids and rung counts only (never bytes or paths).',
  since: '1.19.0',
});

const PACKAGING_FAILED_TYPE = 'media:packaging-failed';
const packagingFailedSchema: EventSchema<MediaPackagingFailedPayload> = {
  parse(data) {
    const record = asEventRecord(data, PACKAGING_FAILED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', PACKAGING_FAILED_TYPE),
      jobId: requireString(record.jobId, 'jobId', PACKAGING_FAILED_TYPE),
      assetId: optionalString(record.assetId, 'assetId', PACKAGING_FAILED_TYPE),
      reason: requireString(record.reason, 'reason', PACKAGING_FAILED_TYPE),
      failedAt: requireString(record.failedAt, 'failedAt', PACKAGING_FAILED_TYPE),
    };
  },
};

export const MEDIA_PACKAGING_FAILED_EVENT = defineEvent<MediaPackagingFailedPayload>({
  type: PACKAGING_FAILED_TYPE,
  version: 1,
  schema: packagingFailedSchema,
  auditable: true,
  description:
    'A media-package job failed (reason string only; no credentials, no filesystem paths).',
  since: '1.19.0',
});

const PLAYBACK_TOKEN_ISSUED_TYPE = 'media:playback-token-issued';
const playbackTokenIssuedSchema: EventSchema<MediaPlaybackTokenIssuedPayload> = {
  parse(data) {
    const record = asEventRecord(data, PLAYBACK_TOKEN_ISSUED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', PLAYBACK_TOKEN_ISSUED_TYPE),
      assetId: requireString(record.assetId, 'assetId', PLAYBACK_TOKEN_ISSUED_TYPE),
      userId: optionalString(record.userId, 'userId', PLAYBACK_TOKEN_ISSUED_TYPE),
      expiresIn: optionalNumber(record.expiresIn, 'expiresIn', PLAYBACK_TOKEN_ISSUED_TYPE),
      issuedAt: requireString(record.issuedAt, 'issuedAt', PLAYBACK_TOKEN_ISSUED_TYPE),
    };
  },
};

export const MEDIA_PLAYBACK_TOKEN_ISSUED_EVENT = defineEvent<MediaPlaybackTokenIssuedPayload>({
  type: PLAYBACK_TOKEN_ISSUED_TYPE,
  version: 1,
  schema: playbackTokenIssuedSchema,
  // High-frequency: minted per playback start (and per token refresh). Same
  // `media:job-progress` precedent — auditable:false, Redis `media:events`
  // channel ONLY, listed in FANOUT_EXCLUDED_EVENT_TYPES so a future durable
  // relay never floods `events:fanout`.
  auditable: false,
  description:
    'A short-lived, title/org-bound playback token was minted (ids and granted seconds only, never the credential). High-frequency; Redis-only transport, excluded from durable fan-out and from audit.',
  since: '1.19.0',
});

export const MEDIA_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [MEDIA_ASSET_REGISTERED_EVENT.type]: MEDIA_ASSET_REGISTERED_EVENT,
  [MEDIA_JOB_QUEUED_EVENT.type]: MEDIA_JOB_QUEUED_EVENT,
  [MEDIA_JOB_PROGRESS_EVENT.type]: MEDIA_JOB_PROGRESS_EVENT,
  [MEDIA_JOB_COMPLETED_EVENT.type]: MEDIA_JOB_COMPLETED_EVENT,
  [MEDIA_JOB_FAILED_EVENT.type]: MEDIA_JOB_FAILED_EVENT,
  [MEDIA_TITLE_PUBLISHED_EVENT.type]: MEDIA_TITLE_PUBLISHED_EVENT,
  [MEDIA_TITLE_SCHEDULED_EVENT.type]: MEDIA_TITLE_SCHEDULED_EVENT,
  [MEDIA_TITLE_HIDDEN_EVENT.type]: MEDIA_TITLE_HIDDEN_EVENT,
  [MEDIA_TITLE_ARCHIVED_EVENT.type]: MEDIA_TITLE_ARCHIVED_EVENT,
  [MEDIA_ASSET_PROBED_EVENT.type]: MEDIA_ASSET_PROBED_EVENT,
  [MEDIA_ASSET_READY_EVENT.type]: MEDIA_ASSET_READY_EVENT,
  [MEDIA_JOB_CANCELLED_EVENT.type]: MEDIA_JOB_CANCELLED_EVENT,
  [MEDIA_SUBTITLE_IMPORTED_EVENT.type]: MEDIA_SUBTITLE_IMPORTED_EVENT,
  [MEDIA_PACKAGING_COMPLETED_EVENT.type]: MEDIA_PACKAGING_COMPLETED_EVENT,
  [MEDIA_PACKAGING_FAILED_EVENT.type]: MEDIA_PACKAGING_FAILED_EVENT,
  [MEDIA_PLAYBACK_TOKEN_ISSUED_EVENT.type]: MEDIA_PLAYBACK_TOKEN_ISSUED_EVENT,
};
